import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CellParams, SortItem } from '@material-ui/data-grid';
import { ServerDataGrid, CurrencyFormattedDisplay, Grid } from '@emburse/embark';

import { CREDITS_PER_PAGE, PER_PAGE_OPTIONS } from '../../constants';
import { CSVExport } from '../Common/CSVExport';
import { formHeaders } from '../../helpers/csv';
import { CreditSource, ISerializedCredit } from '../../types/api/serialized_credit';
import { dateTimeFormatter } from '../../helpers/datagrid_formatters';
import { useApi, usePagination } from '../../hooks';
import { listCreditsForCompany } from '../../api/routes';
import { ISerializedUser } from '../../types/api/serialized_user';
import { Spacer } from '../Common/UI';
import { FilterBar } from '../Common/FilterBar';
import { ListCreditsWhere } from '../../api/routes/credits';

interface ICreditListProps {
  companyId: string;
  currency: string;
}

const sourceOptions: { label: string; value: CreditSource; }[] = [
  { label: 'Reimbursement', value: 'reimbursement' },
  { label: 'Trial Deposit', value: 'trial_deposit' },
  { label: 'Bill Refund', value: 'bill_refund' },
  { label: 'Reimbursement Refund', value: 'reimbursement_refund' },
  { label: 'Payment', value: 'payment' }
];

function CompanyCreditList({ companyId, currency }: ICreditListProps) {
  const [where, setWhere] = useState<ListCreditsWhere>({});
  const [order, setOrder] =  useState<SortItem>({ field: 'created_at', sort: 'desc' });
  const { limit, offset, onPageChange } = usePagination(CREDITS_PER_PAGE);
  const { items, loading } = useApi<ISerializedCredit>(listCreditsForCompany(companyId, limit, offset, where, order));

  const creditColumns = useMemo(() => [
    { field: 'id', hide: true },
    {
      field: 'amount', headerName: 'Amount', width: 140,
      renderCell: function render(params: CellParams) {
        const amount = params.getValue('amount') as number / 100;
        const creditId = params.getValue('id') as string;
        return (
          <Link to={`/companies/${companyId}/credits/${creditId}`}>
            <CurrencyFormattedDisplay amount={amount} code={currency} />
          </Link>
        );
      }
    },
    { field: 'created_at', headerName: 'Create Date', type: 'dateTime', width: 240, valueFormatter: dateTimeFormatter },
    { field: 'source', headerName: 'Source', width: 300 },
    { field: 'user_id', hide: true },
    {
      field: 'user',
      headerName: 'User', width: 300,
      renderCell: function render(params: CellParams) {
        const user = params.getValue('user') as ISerializedUser;
        return <>{user.name}</>;
      }
    },
    { field: 'status', headerName: 'Status' }
  ], [companyId, currency]);

  const onSortChange = (sortData: SortItem[]) => {
    setOrder((sortData && sortData[0]) || null);
  };

  const handleFilter = (source: string) => {
    onPageChange(0, limit);
    setWhere({ ...where, source });
  };

  return (
    <>
      <Spacer />
      <Grid container justify="flex-end" spacing={2}>
        <Grid item>
          <FilterBar label="Filter by source" options={sourceOptions} onFilter={handleFilter} />
        </Grid>
      </Grid>
      <Spacer />
      <ServerDataGrid
        rowCount={items.count}
        rows={items.rows}
        columns={creditColumns}
        autoHeight={true}
        rowsPerPageOptions={PER_PAGE_OPTIONS}
        pageSize={limit}
        loading={loading}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
      />
      <CSVExport
        route={listCreditsForCompany(companyId, limit, offset, where, order)}
        headers={formHeaders(creditColumns, { omit: ['user'] })}
        title="Export Credits to CSV"
      />
    </>
  );
}

export { CompanyCreditList };
